// =============================================================================
// screens/VoiceCallScreen.tsx — Full-screen 1:1 video/audio call (LiveKit-backed)
//
// Bare-RN port of apps/cryb-native/app/voice/call.tsx (P3-5 CALLING group).
//
// MIGRATION (blueprint §1):
//   - expo-router (useLocalSearchParams/useRouter/Stack.Screen)
//       -> react-navigation: route.params (typed RootStackParamList['VoiceCall'])
//          + useNavigation().goBack(). No <Stack.Screen> — this screen is
//          registered in App.tsx with presentation:'fullScreenModal' +
//          headerShown:false, so the per-file Stack.Screen options drop out.
//   - expo-image  -> react-native Image (transition fade dropped).
//   - @expo/vector-icons (Ionicons) -> components/ui/Icon.
//   - @livekit/react-native works in bare RN AS-IS (registerGlobals() runs in
//     index.js BEFORE App). AudioSession + LiveKitRoom + room hooks unchanged.
//
// PRESERVED VERBATIM (LR8 call signaling — do NOT alter):
//   - push-ring auto-join (POST /voice/video/join/:callId mints a token),
//   - caller-side onUserEvent signaling (callDeclined/callTimeout/callBusy/
//     callCancelled) so "Ringing…" never hangs,
//   - hang-up-while-ringing → cancel/decline so the other side dismisses,
//   - mid-call reconnect (remount the room up to MAX_RECONNECT_ATTEMPTS),
//   - audio-only path (mount LiveKitRoom with video disabled + avatar UI).
//
// PARAM TYPING NOTE: the navigation contract (navigation/types.ts) types
// `isCaller`/`autoJoin` as booleans (not the expo-router '1'/'true' strings),
// so the guards below accept both shapes (a push-routed call may still pass
// strings through the deep-link layer).
// =============================================================================
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ActivityIndicator,
  Alert,
  Platform,
  NativeModules,
  findNodeHandle,
  Animated,
  Easing,
  useWindowDimensions,
} from 'react-native';
import CachedImage from '../components/ui/CachedImage';
import { useNavigation, useRoute, type RouteProp } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import {
  LiveKitRoom,
  VideoTrack,
  useTracks,
  useRoomContext,
  isTrackReference,
  type TrackReference,
} from '@livekit/react-native';
import { ScreenCapturePickerView } from '@livekit/react-native-webrtc';
import { Track, RoomEvent } from 'livekit-client';
import { Ionicons } from '../components/ui/Icon';
import Screen from '../components/ui/Screen';
import Header from '../components/ui/Header';
import Safe from '../components/Safe';
import ParticleBackground from '../components/ui/ParticleBackground';
import { GlassSurface } from '../components/glass';
import type { RootStackParamList } from '../navigation/types';
import { colors, colorTokens, spacing, radius, fonts, getLayout } from '../constants/theme';
import { api } from '../services/api';
import { onUserEvent } from '../services/socket';
import {
  ensureLiveKitGlobals,
  prepareVoiceAudio,
  scheduleVoiceAudioStop,
  setVoiceSpeaker,
  runGuardedTeardown,
} from '../services/livekit-globals';
import { notifyScreenShareStart, notifyScreenShareStop } from '../services/screenShare';
import ConnectionBanner from '../components/voice/ConnectionBanner';
import ConnectionQualityBadge from '../components/voice/ConnectionQualityBadge';
import { setActiveCall, clearActiveCall } from '../components/call/CallInProgressBanner';

// NOTE: ensureLiveKitGlobals() is NOT called at module scope — running WebRTC
// native init during Hermes module-eval (the lazy require in App.tsx) can throw in
// a release archive → require(...).default becomes undefined → "Cannot read
// property 'default' of undefined". It's called inside the component (mount effect).

const MAX_RECONNECT_ATTEMPTS = 5;

// In-call emoji reactions (published over the LiveKit data channel as a small
// JSON packet). Shared by CallBody here and RoomBody in VoiceRoomScreen.
const REACTION_EMOJI = ['❤️', '😂', '👍', '🔥', '👏', '🎉'] as const;
const REACTION_TOPIC = 'cryb-reaction';

// UTF-8 (de)serialization for the data-channel payload. We avoid TextEncoder/
// TextDecoder (not guaranteed to be polyfilled in RN) by round-tripping through
// unescape(encodeURIComponent(...)) / decodeURIComponent(escape(...)) — safe for
// the short JSON reaction packets (incl. multi-byte emoji).
function encodeUtf8(str: string): Uint8Array {
  const bin = unescape(encodeURIComponent(str));
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}
function decodeUtf8(bytes: Uint8Array): string {
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return decodeURIComponent(escape(bin));
}

type Nav = NativeStackNavigationProp<RootStackParamList, 'VoiceCall'>;
type CallRoute = RouteProp<RootStackParamList, 'VoiceCall'>;

// Accept both the typed boolean and the legacy '1'/'true' string a deep-link
// (push-tap) path may still pass through.
const isTruthy = (v: unknown): boolean => v === true || v === '1' || v === 'true';

export default function VoiceCallScreen() {
  const navigation = useNavigation<Nav>();
  const { params } = useRoute<CallRoute>();

  const callId = typeof params?.callId === 'string' ? params.callId : '';
  const peerName = typeof params?.peerName === 'string' ? params.peerName : 'On a call';
  const peerAvatar = typeof params?.peerAvatar === 'string' ? params.peerAvatar : '';
  const isCaller = isTruthy(params?.isCaller);
  // Honor an audio-only call: mount LiveKitRoom with video disabled + audio UI.
  const isAudioOnly = params?.media === 'audio';
  // From a push tap we arrive with a callId but NO token — auto-join to mint one.
  const needsAutoJoin = isTruthy(params?.autoJoin) && !params?.token && !!callId;

  // Token/url may come in via params (foreground accept / caller init) OR be
  // minted here by an auto-join (push tap). Hold them in state so auto-join can
  // fill them in before we mount the room.
  const [token, setToken] = useState<string>(typeof params?.token === 'string' ? params.token : '');
  const [url, setUrl] = useState<string>(typeof params?.url === 'string' ? params.url : '');
  const [joining, setJoining] = useState<boolean>(needsAutoJoin);

  const [error, setError] = useState<string | null>(null);
  // Caller-side call lifecycle ("Ringing…" never hangs): a terminal signal
  // (declined / timed out / busy / cancelled) flips this to a labeled end state.
  const [callEnded, setCallEnded] = useState<{ label: string; sub: string } | null>(null);
  // Mid-call reconnect (LR8): onError/disconnect bumps this; we remount the room
  // (same still-valid token) up to MAX_RECONNECT_ATTEMPTS before giving up.
  const [reconnectKey, setReconnectKey] = useState(0);
  const reconnectAttempts = useRef(0);
  const [reconnecting, setReconnecting] = useState(false);

  // AUDIO ROOT-CAUSE FIX: configure the AVAudioSession (playAndRecord +
  // videoChat/voiceChat) BEFORE activating it and BEFORE the room connects —
  // a bare startAudioSession() only did setActive(true) with the wrong/default
  // category, which is why calls connected with video but no audio either way.
  // The room mount below is gated on `audioReady` so the canonical order
  // (configure → setAppleAudioConfiguration → start → connect) always holds.
  const [audioReady, setAudioReady] = useState(false);
  useEffect(() => {
    // Register WebRTC globals HERE (inside the component), not at module scope, so
    // evaluating this module during the lazy require never touches native.
    ensureLiveKitGlobals();
    let alive = true;
    prepareVoiceAudio(isAudioOnly ? 'audioCall' : 'videoCall').finally(() => {
      if (alive) setAudioReady(true);
    });
    return () => {
      alive = false;
      // CRASH FIX (teardown order): never setActive(false) while the room is
      // still tearing down. The explicit end-call flow stops the session after
      // disconnect resolves; this delayed stop only covers exits that bypass
      // it (it no-ops if the session was already stopped or re-prepared).
      scheduleVoiceAudioStop();
    };
  }, [isAudioOnly]);

  // Auto-join from a push tap: POST /voice/video/join/:callId to mint a token.
  useEffect(() => {
    if (!needsAutoJoin) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api<any>(`/voice/video/join/${callId}`, {
          method: 'POST',
          body: JSON.stringify({ videoEnabled: !isAudioOnly, audioEnabled: true }),
        });
        if (cancelled) return;
        const t = res?.data?.token;
        const u = res?.data?.liveKitUrl || res?.data?.url;
        if (res?.success && t && u) {
          setToken(t);
          setUrl(u);
        } else {
          setCallEnded({ label: 'Call ended', sub: 'This call is no longer available.' });
        }
      } catch {
        if (!cancelled) setCallEnded({ label: 'Call failed', sub: 'Could not join the call.' });
      } finally {
        if (!cancelled) setJoining(false);
      }
    })().catch((e) => {
      // Belt-and-suspenders: the inner try/catch already handles the join, but a
      // failure anywhere else in the async flow must not surface as an unhandled
      // promise rejection (which can crash the screen). Log + fall back gracefully.
      console.warn('[voicecall] auto-join failed:', e);
      if (!cancelled) {
        setCallEnded({ label: 'Call failed', sub: 'Could not join the call.' });
        setJoining(false);
      }
    });
    return () => { cancelled = true; };
  }, [needsAutoJoin, callId, isAudioOnly]);

  // CALLER-SIDE SIGNALING (LR8): while ringing, listen for the callee's response
  // so "Ringing…" resolves instead of hanging. Each event ends the call with a
  // clear label. Listeners are reconnect-safe (onUserEvent rebinds on reconnect).
  useEffect(() => {
    if (!callId) return;
    const matches = (p: any) => !p?.callId || p.callId === callId;
    const unsubs = [
      onUserEvent('callDeclined', (p) => { if (matches(p)) setCallEnded({ label: 'Call declined', sub: `${peerName} declined the call.` }); }),
      onUserEvent('callTimeout', (p) => { if (matches(p)) setCallEnded({ label: 'No answer', sub: `${peerName} didn’t answer.` }); }),
      onUserEvent('callBusy', (p) => { if (matches(p)) setCallEnded({ label: 'Busy', sub: `${peerName} is on another call.` }); }),
      onUserEvent('callCancelled', (p) => { if (matches(p)) setCallEnded({ label: 'Call ended', sub: 'The call was cancelled.' }); }),
    ];
    return () => { unsubs.forEach((u) => { try { u(); } catch {} }); };
  }, [callId, peerName]);

  // If WE back out while still ringing, tell the other side (caller→cancel,
  // callee→decline) so their UI dismisses. Fired from the end button pre-connect.
  const signalHangupWhileRinging = useCallback(() => {
    if (!callId) return;
    const path = isCaller ? `/voice/video/cancel/${callId}` : `/voice/video/decline/${callId}`;
    api<any>(path, { method: 'POST' }).catch(() => {});
  }, [callId, isCaller]);

  // Mid-call error/disconnect handler: instead of a terminal error screen, enter
  // a "Reconnecting…" state and remount the room (the LiveKit token is still
  // valid for the room's lifetime). Give up after MAX_RECONNECT_ATTEMPTS.
  const handleRoomError = useCallback((e: Error) => {
    console.warn('[livekit] call room error:', e);
    if (callEnded) return;
    if (reconnectAttempts.current >= MAX_RECONNECT_ATTEMPTS) {
      setError(e?.message || 'Connection lost');
      return;
    }
    reconnectAttempts.current += 1;
    setReconnecting(true);
    const delay = Math.min(1000 * reconnectAttempts.current, 4000);
    setTimeout(() => setReconnectKey((k) => k + 1), delay);
  }, [callEnded]);

  // Called by CallBody once the room reports connected — clears any reconnect UI.
  const handleConnected = useCallback(() => {
    reconnectAttempts.current = 0;
    setReconnecting(false);
  }, []);

  const goBack = useCallback(() => {
    // Even on the pre-connect terminal screens, guarantee the audio session is
    // torn down (a bare setActive teardown may be pending) before we pop.
    try { scheduleVoiceAudioStop(); } catch {}
    navigation.goBack();
  }, [navigation]);

  // Terminal call-state screen (declined / no-answer / busy / cancelled / ended).
  if (callEnded) {
    return (
      <StatusScreen
        icon="call-outline"
        iconColor={colorTokens.dark.textFaint}
        title={callEnded.label}
        subtitle={callEnded.sub}
        actionLabel="Close"
        onAction={goBack}
      />
    );
  }

  // Auto-joining (push tap) or waiting for the audio session to be configured —
  // the room must not mount before the audio config is applied (see above).
  if (joining || !audioReady) {
    return (
      <StatusScreen
        loading
        subtitle="Joining call…"
        onBack={goBack}
        actionLabel="Cancel"
        onAction={goBack}
      />
    );
  }

  // Crash-safe guard: without a token + url there is nothing to connect to.
  if (!token || !url) {
    return (
      <StatusScreen
        icon="videocam-off-outline"
        iconColor={colorTokens.dark.error}
        title="Call unavailable"
        subtitle="This call is no longer available."
        actionLabel="Go back"
        onAction={goBack}
      />
    );
  }

  // Only a give-up after exhausting reconnects reaches the terminal error screen.
  if (error) {
    return (
      <StatusScreen
        icon="alert-circle-outline"
        iconColor={colorTokens.dark.error}
        title="Call error"
        subtitle={error}
        actionLabel="End call"
        onAction={goBack}
      />
    );
  }

  return (
    <View style={styles.root}>
      <LiveKitRoom
        key={reconnectKey}
        serverUrl={url}
        token={token}
        connect={true}
        audio={true}
        video={!isAudioOnly}
        onError={handleRoomError}
      >
        <CallBody
          callId={callId}
          peerName={peerName}
          peerAvatar={peerAvatar}
          isCaller={isCaller}
          isAudioOnly={isAudioOnly}
          reconnecting={reconnecting}
          onConnected={handleConnected}
          onHangupWhileRinging={signalHangupWhileRinging}
        />
      </LiveKitRoom>
    </View>
  );
}

// Shared dark-themed status/terminal screen (joining / ended / unavailable /
// error). Uses the design-system Screen + Header in dark mode so every non-live
// call state has consistent brand background, spacing and typography — no
// ad-hoc dark hexes or bespoke layouts.
function StatusScreen({
  icon,
  iconColor,
  title,
  subtitle,
  actionLabel,
  onAction,
  onBack,
  loading = false,
}: {
  icon?: string;
  iconColor?: string;
  title?: string;
  subtitle?: string;
  actionLabel?: string;
  onAction?: () => void;
  onBack?: () => void;
  loading?: boolean;
}) {
  const tc = colorTokens.dark;
  return (
    <Screen theme="dark" blobs={false} scroll={false} edges={['top', 'bottom']}>
      {/* Aurora backdrop (dark) — replaces the flat dark fill so terminal call
          states share the platform's ambient depth. Purely decorative. */}
      <ParticleBackground theme="dark" intensity={0.4} count={4} />
      {onBack ? <Header theme="dark" titleSize="default" onBack={onBack} /> : null}
      <View style={styles.center}>
        {loading ? (
          <ActivityIndicator size="large" color={tc.primary} />
        ) : icon ? (
          <Ionicons name={icon} size={48} color={iconColor ?? tc.textFaint} />
        ) : null}
        {title ? <Text style={styles.endedTitle}>{title}</Text> : null}
        {subtitle ? <Text style={styles.errorText}>{subtitle}</Text> : null}
        {actionLabel && onAction ? (
          <TouchableOpacity
            onPress={onAction}
            style={styles.backBtn}
            accessibilityRole="button"
            accessibilityLabel={actionLabel}
          >
            <Text style={styles.backBtnText}>{actionLabel}</Text>
          </TouchableOpacity>
        ) : null}
      </View>
    </Screen>
  );
}

// ── In-call emoji reactions ───────────────────────────────────────────────────
// A live emoji reaction (its own float animation) rising up the screen.
interface FloatingReaction {
  id: string;
  emoji: string;
}

// Hook: publish/receive reactions over the room's data channel. Returns the
// live queue + a send fn. Must be used INSIDE <LiveKitRoom> (uses the room ctx).
function useReactions(roomCtx: ReturnType<typeof useRoomContext>) {
  const [reactions, setReactions] = useState<FloatingReaction[]>([]);
  const seq = useRef(0);

  const push = useCallback((emoji: string) => {
    const id = `${Date.now()}-${seq.current++}`;
    setReactions((prev) => [...prev, { id, emoji }]);
  }, []);

  const remove = useCallback((id: string) => {
    setReactions((prev) => prev.filter((r) => r.id !== id));
  }, []);

  // Receive peers' reactions.
  useEffect(() => {
    if (!roomCtx) return;
    const onData = (payload: Uint8Array, _p: any, _k: any, topic?: string) => {
      if (topic && topic !== REACTION_TOPIC) return;
      try {
        const msg = JSON.parse(decodeUtf8(payload));
        if (msg?.type === 'reaction' && typeof msg.emoji === 'string') push(msg.emoji);
      } catch {
        /* ignore non-reaction / malformed packets */
      }
    };
    roomCtx.on(RoomEvent.DataReceived, onData as any);
    return () => {
      try { roomCtx.off(RoomEvent.DataReceived, onData as any); } catch {}
    };
  }, [roomCtx, push]);

  // Send + show locally (optimistic).
  const send = useCallback((emoji: string) => {
    push(emoji);
    try {
      const data = encodeUtf8(JSON.stringify({ type: 'reaction', emoji }));
      // reliable:false — a dropped reaction is harmless and shouldn't block media.
      void roomCtx?.localParticipant?.publishData(data as any, { reliable: false, topic: REACTION_TOPIC });
    } catch (e) {
      console.warn('[livekit] publish reaction failed:', e);
    }
  }, [roomCtx, push]);

  return { reactions, send, remove };
}

// One rising emoji — floats up + fades, then self-removes.
function RisingEmoji({ emoji, onDone }: { emoji: string; onDone: () => void }) {
  const t = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    Animated.timing(t, {
      toValue: 1,
      duration: 2200,
      easing: Easing.out(Easing.quad),
      useNativeDriver: true,
    }).start(() => onDone());
  }, [t, onDone]);
  // Small random horizontal drift so stacked reactions don't overlap perfectly.
  const drift = useRef((Math.random() - 0.5) * 60).current;
  return (
    <Animated.Text
      style={[
        styles.risingEmoji,
        {
          opacity: t.interpolate({ inputRange: [0, 0.15, 0.85, 1], outputRange: [0, 1, 1, 0] }),
          transform: [
            { translateY: t.interpolate({ inputRange: [0, 1], outputRange: [0, -220] }) },
            { translateX: t.interpolate({ inputRange: [0, 1], outputRange: [0, drift] }) },
            { scale: t.interpolate({ inputRange: [0, 0.2, 1], outputRange: [0.6, 1.2, 1] }) },
          ],
        },
      ]}
    >
      {emoji}
    </Animated.Text>
  );
}

// The floating layer (rising emojis) — absolutely positioned, non-interactive.
function ReactionOverlay({ reactions, onDone }: { reactions: FloatingReaction[]; onDone: (id: string) => void }) {
  return (
    <View style={styles.reactionOverlay} pointerEvents="none">
      {reactions.map((r) => (
        <RisingEmoji key={r.id} emoji={r.emoji} onDone={() => onDone(r.id)} />
      ))}
    </View>
  );
}

// The tappable emoji row shown above the control bar.
function ReactionsRow({ onSend }: { onSend: (emoji: string) => void }) {
  return (
    <View style={styles.reactionsRow}>
      {REACTION_EMOJI.map((e) => (
        <TouchableOpacity
          key={e}
          style={styles.reactionBtn}
          onPress={() => onSend(e)}
          activeOpacity={0.7}
          accessibilityRole="button"
          accessibilityLabel={`React with ${e}`}
        >
          <Text style={styles.reactionEmoji}>{e}</Text>
        </TouchableOpacity>
      ))}
    </View>
  );
}

// Inner component: must live INSIDE <LiveKitRoom> so the room hooks resolve.
function CallBody({
  callId,
  peerName,
  peerAvatar,
  isCaller,
  isAudioOnly,
  reconnecting,
  onConnected,
  onHangupWhileRinging,
}: {
  callId: string;
  peerName: string;
  peerAvatar: string;
  isCaller: boolean;
  isAudioOnly: boolean;
  reconnecting: boolean;
  onConnected: () => void;
  onHangupWhileRinging: () => void;
}) {
  const navigation = useNavigation<Nav>();
  const insets = useSafeAreaInsets();
  const roomCtx = useRoomContext();

  // Responsive centering (shared pattern): FULL-BLEED media stays edge-to-edge,
  // but the control cluster + participant tiles get width-capped + centered on
  // tablet/desktop so they don't stretch across an iPad. Phone = unchanged.
  const { width } = useWindowDimensions();
  const layout = getLayout(width);
  const contentMaxWidth = layout === 'desktop' ? 720 : 620;
  const containerStyle =
    layout !== 'phone'
      ? {
          maxWidth: contentMaxWidth,
          alignSelf: 'center' as const,
          width: '100%' as const,
        }
      : undefined;
  // PiP hugs the right edge of the centered column: screen-side gutter + the
  // phone inset. On phone the gutter is 0, so this is just the original inset.
  const columnGutter = containerStyle ? Math.max((width - contentMaxWidth) / 2, 0) : 0;
  const pipRight = columnGutter + spacing.lg;

  // Subscribe to camera + screen-share video tracks; we pick out the remote and
  // local references below.
  const tracks = useTracks([
    { source: Track.Source.Camera, withPlaceholder: false },
    { source: Track.Source.ScreenShare, withPlaceholder: false },
  ]);

  const [isMicOn, setIsMicOn] = useState(true);
  const [isCamOn, setIsCamOn] = useState(true);
  const [togglingMic, setTogglingMic] = useState(false);
  const [togglingCam, setTogglingCam] = useState(false);
  const [flipping, setFlipping] = useState(false);
  // Current front/back camera facing; toggled on each successful flip.
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('user');

  // Screen-share state. `isSharing` reflects our own publish; screenIdentityRef
  // holds the server-minted screen_* identity (see services/screenShare.ts).
  const [isSharing, setIsSharing] = useState(false);
  const [togglingShare, setTogglingShare] = useState(false);
  const screenIdentityRef = useRef<string | null>(null);
  // The ReplayKit system broadcast picker must exist in the view tree (iOS).
  const screenCaptureRef = useRef(null);

  // Audio-only calls default to the earpiece (phone-like); this toggles the
  // loudspeaker via the SDK's output override (see setVoiceSpeaker).
  const [speakerOn, setSpeakerOn] = useState(false);

  const localIdentity = roomCtx?.localParticipant?.identity;
  // The actual LiveKit room name we're connected to (e.g. video_call_<callId>).
  // Used by the best-effort backend screen-share signaling — no convention guess.
  const roomName = roomCtx?.name ?? '';

  // Split tracks into local (PiP) camera vs remote camera vs remote screen share.
  // A remote screen share, when present, takes over the full-screen view.
  const { localCam, remoteCam, remoteScreen } = useMemo(() => {
    const list = Array.isArray(tracks) ? tracks : [];
    let local: TrackReference | undefined;
    let remote: TrackReference | undefined;
    let screen: TrackReference | undefined;
    for (const t of list) {
      if (!isTrackReference(t)) continue;
      const ident = t.participant?.identity;
      const isLocal = !!ident && ident === localIdentity;
      if (t.source === Track.Source.ScreenShare) {
        // Only show OTHER people's screen full-screen (our own is already on-device).
        if (!isLocal) screen = t as TrackReference;
        continue;
      }
      if (isLocal) {
        local = t as TrackReference;
      } else {
        remote = t as TrackReference;
      }
    }
    return { localCam: local, remoteCam: remote, remoteScreen: screen };
  }, [tracks, localIdentity]);

  // Whether anyone other than us is in the room yet (drives "Ringing…" vs live).
  const remoteJoined = (roomCtx?.remoteParticipants?.size ?? 0) > 0;

  // In-call emoji reactions (data-channel publish/receive + float animation).
  const { reactions, send: sendReaction, remove: removeReaction } = useReactions(roomCtx);

  // Once the remote joins, the media path is up — clear any reconnect UI in the
  // parent (resets the reconnect-attempt counter). Fires on every (re)connect.
  useEffect(() => {
    if (remoteJoined) onConnected();
  }, [remoteJoined, onConnected]);

  // Drive the app-wide "call in progress — tap to return" banner: on once the
  // media path is up, off when this screen unmounts (any exit path). The banner
  // lets the user leave the call UI and jump back from anywhere in the app.
  useEffect(() => {
    if (!remoteJoined) return;
    setActiveCall({
      kind: 'call',
      route: 'VoiceCall',
      params: { callId, peerName, peerAvatar, media: isAudioOnly ? 'audio' : undefined },
      title: peerName,
      subtitle: isAudioOnly ? 'Audio call' : 'Video call',
    });
  }, [remoteJoined, peerName, peerAvatar, isAudioOnly]);
  useEffect(() => () => { clearActiveCall(); }, []);

  // Mic toggle — guard re-entrancy + missing local participant; commit optimistic
  // flag only after the SDK call resolves so the button can't desync.
  const handleToggleMic = useCallback(async () => {
    if (togglingMic || !roomCtx?.localParticipant) return;
    setTogglingMic(true);
    const next = !isMicOn;
    try {
      await roomCtx.localParticipant.setMicrophoneEnabled(next);
      setIsMicOn(next);
    } catch (e) {
      console.warn('[livekit] setMicrophoneEnabled failed:', e);
    } finally {
      setTogglingMic(false);
    }
  }, [togglingMic, isMicOn, roomCtx]);

  const handleToggleCam = useCallback(async () => {
    if (togglingCam || !roomCtx?.localParticipant) return;
    setTogglingCam(true);
    const next = !isCamOn;
    try {
      await roomCtx.localParticipant.setCameraEnabled(next);
      setIsCamOn(next);
    } catch (e) {
      console.warn('[livekit] setCameraEnabled failed:', e);
    } finally {
      setTogglingCam(false);
    }
  }, [togglingCam, isCamOn, roomCtx]);

  // Flip front/back camera by restarting the camera track with a toggled
  // facingMode — the official LiveKit LocalVideoTrack API (more portable than
  // react-native-webrtc's switchCamera). Guarded; no-op if no camera publishing.
  const handleFlipCamera = useCallback(async () => {
    if (flipping || !roomCtx?.localParticipant) return;
    setFlipping(true);
    const next = facingMode === 'user' ? 'environment' : 'user';
    try {
      const pub = roomCtx.localParticipant.getTrackPublication?.(Track.Source.Camera);
      const track: any = pub?.track;
      if (track && typeof track.restartTrack === 'function') {
        await track.restartTrack({ facingMode: next });
        setFacingMode(next);
      }
    } catch (e) {
      console.warn('[livekit] flip camera failed:', e);
    } finally {
      setFlipping(false);
    }
  }, [flipping, roomCtx, facingMode]);

  // Toggle screen sharing. On iOS this must (1) trigger the system ReplayKit
  // broadcast picker so the user authorizes capture into our broadcast extension,
  // then (2) publish the screen track on our EXISTING room connection. The backend
  // start/stop calls are best-effort peer signaling (services/screenShare.ts).
  const handleToggleScreenShare = useCallback(async () => {
    if (togglingShare || !roomCtx?.localParticipant) return;
    setTogglingShare(true);
    try {
      if (!isSharing) {
        if (Platform.OS === 'ios') {
          const reactTag = findNodeHandle(screenCaptureRef.current);
          if (reactTag != null) {
            // Pops the system "Start Broadcast" sheet for the CRYB extension.
            // Optional-chained: the manager is absent until the Broadcast Upload
            // Extension target ships, so a build without it degrades, not crashes.
            await NativeModules.ScreenCapturePickerViewManager?.show?.(reactTag);
          }
        }
        await roomCtx.localParticipant.setScreenShareEnabled(true);
        setIsSharing(true);
        const { identity } = await notifyScreenShareStart(roomName, { quality: 'medium', frameRate: 15 });
        screenIdentityRef.current = identity;
      } else {
        await roomCtx.localParticipant.setScreenShareEnabled(false);
        setIsSharing(false);
        await notifyScreenShareStop(roomName, screenIdentityRef.current);
        screenIdentityRef.current = null;
      }
    } catch (e) {
      console.warn('[livekit] screen share toggle failed:', e);
    } finally {
      setTogglingShare(false);
    }
  }, [togglingShare, isSharing, roomCtx, roomName]);

  // ---------------------------------------------------------------------------
  // SEQUENCED + TIMEOUT-GUARDED TEARDOWN (crash-on-close fix). The old code
  // stopped the audio session while <LiveKitRoom> was still disconnecting — the
  // known iOS double-stop / dealloc EXC_BAD_ACCESS family. Order is preserved:
  //   1. room.disconnect() and AWAIT it,
  //   2. short settle delay so the native audio engine fully stops,
  //   3. stopVoiceAudio() (single-shot guarded in the service),
  //   4. navigate away exactly ONCE.
  // runGuardedTeardown() wraps EVERY step in try/catch and races the whole
  // thing against MAX_TEARDOWN_MS, so a hung disconnect/stop can no longer
  // freeze the screen (white/black trap) — we force-navigate back regardless.
  // It also always arms scheduleVoiceAudioStop() as a safety net so the audio
  // session is guaranteed to deactivate. `teardownPromiseRef` memoizes the
  // teardown so hangup button + back gesture/pop can't double-run it.
  // ---------------------------------------------------------------------------
  const teardownPromiseRef = useRef<Promise<void> | null>(null);
  const tornDownRef = useRef(false);
  const navDoneRef = useRef(false);
  // Drives the "Ending call…" closing overlay so the user never sees a frozen
  // live UI (or a blank flash) while teardown runs.
  const [closing, setClosing] = useState(false);
  // Keep the latest ring-state in a ref so teardown (memoized once) reads fresh.
  const remoteJoinedRef = useRef(remoteJoined);
  remoteJoinedRef.current = remoteJoined;

  const teardown = useCallback((): Promise<void> => {
    if (!teardownPromiseRef.current) {
      teardownPromiseRef.current = (async () => {
        // If we hang up while still ringing (no remote yet), signal the other
        // side (caller→cancel / callee→decline) so their "Ringing…" dismisses.
        if (!remoteJoinedRef.current) {
          try { onHangupWhileRinging(); } catch {}
        }
        // Guarded: disconnect → settle → stopVoiceAudio, bounded by
        // MAX_TEARDOWN_MS; always arms scheduleVoiceAudioStop() internally.
        await runGuardedTeardown(() => roomCtx?.disconnect());
        tornDownRef.current = true;
      })();
    }
    return teardownPromiseRef.current;
  }, [roomCtx, onHangupWhileRinging]);

  // Navigate away exactly once, whichever exit path completes first.
  const finishNav = useCallback((action?: any) => {
    if (navDoneRef.current) return;
    navDoneRef.current = true;
    try {
      if (action) navigation.dispatch(action);
      else navigation.goBack();
    } catch {}
  }, [navigation]);

  // Intercept EVERY removal (header back, Android back, gesture, goBack) and
  // run the guarded teardown first — the pop only proceeds once it's done (or
  // the timeout elapses). Show the closing overlay meanwhile.
  useEffect(() => {
    const unsub = (navigation as any).addListener('beforeRemove', (e: any) => {
      if (tornDownRef.current) return; // teardown complete — allow the pop
      e.preventDefault();
      setClosing(true);
      void teardown().then(() => finishNav(e.data?.action));
    });
    return unsub;
  }, [navigation, teardown, finishNav]);

  const doEnd = useCallback(async () => {
    setClosing(true);
    await teardown();
    finishNav();
  }, [teardown, finishNav]);

  const onEnd = useCallback(() => {
    Alert.alert('End call', 'Are you sure you want to end this call?', [
      { text: 'Cancel', style: 'cancel' },
      { text: 'End', style: 'destructive', onPress: () => { void doEnd(); } },
    ]);
  }, [doEnd]);

  // Speaker/earpiece toggle — audio-only calls (video calls already default to
  // the loudspeaker via the videoChat session mode).
  const handleToggleSpeaker = useCallback(() => {
    const next = !speakerOn;
    setSpeakerOn(next);
    void setVoiceSpeaker(next);
  }, [speakerOn]);

  const statusLabel = reconnecting
    ? 'Reconnecting…'
    : remoteJoined
      ? 'Connected'
      : isCaller
        ? 'Ringing…'
        : 'Connecting…';

  return (
    <View style={styles.container}>
      {/* The system ReplayKit broadcast picker. Must be present (iOS) in the view
          tree so ScreenCapturePickerViewManager.show(tag) can trigger it. It is
          visually inert (0-size). Wrapped in <Safe>: legacy Paper native view; a
          render failure under New Architecture must not take down the whole call. */}
      {Platform.OS === 'ios' && (
        <Safe label="ScreenCapturePickerView">
          <View style={styles.hiddenPicker} pointerEvents="none">
            <ScreenCapturePickerView ref={screenCaptureRef} />
          </View>
        </Safe>
      )}

      {/* Full-screen view priority: a remote screen share (contain, so nothing is
          cropped) > remote camera (cover) > avatar placeholder. Audio-only calls
          always show the avatar — there is no remote video to render. */}
      {!isAudioOnly && remoteScreen ? (
        <VideoTrack trackRef={remoteScreen} style={styles.remoteVideo} objectFit="contain" />
      ) : !isAudioOnly && remoteCam ? (
        <VideoTrack trackRef={remoteCam} style={styles.remoteVideo} objectFit="cover" />
      ) : (
        <View style={styles.remotePlaceholder}>
          {/* Aurora stage behind the avatar (dark media preset). Skipped only for
              full-screen live camera/screen-share above — this is the audio-only /
              pre-connect state, so we want depth, not flat black. */}
          <ParticleBackground theme="dark" intensity={0.35} count={4} />
          <View style={styles.remoteAvatarRing}>
            {peerAvatar ? (
              <CachedImage source={{ uri: peerAvatar }} style={styles.remoteAvatar} />
            ) : (
              <View style={[styles.remoteAvatar, styles.remoteAvatarFallback]}>
                <Text style={styles.remoteAvatarLetter}>
                  {(peerName || '?').charAt(0).toUpperCase()}
                </Text>
              </View>
            )}
          </View>
        </View>
      )}

      {/* Top glass status bar: peer name + call state + live network quality.
          Translucent dark glass — content (video/avatar) scrolls under it. */}
      <GlassSurface
        mode="dark"
        variant="bar"
        radius={0}
        sheen={false}
        shadow={false}
        style={styles.topBar}
        contentStyle={[styles.topBarInner, containerStyle, { paddingTop: insets.top + spacing.sm }]}
      >
        <Text style={styles.peerName} numberOfLines={1}>{peerName}</Text>
        <View style={styles.statusRow}>
          {remoteJoined && <View style={styles.liveDot} />}
          <Text style={styles.statusText}>{statusLabel}</Text>
          {/* Live connection-quality pill (RTCStats-derived). Self-hides when
              the connection is strong; warns/reds when it degrades. */}
          <ConnectionQualityBadge />
        </View>
      </GlassSurface>

      {/* Live connection state (connecting / reconnecting / dropped). */}
      <ConnectionBanner topOffset={insets.top + 64} />

      {/* Local camera PiP (top-right). Suppressed entirely on audio-only calls.
          On tablet/desktop it hugs the right edge of the centered content column
          (not the screen edge) so it tracks the width-capped control cluster. */}
      {!isAudioOnly && (
        isCamOn && localCam ? (
          <View style={[styles.pip, { top: insets.top + 72, right: pipRight }]}>
            <VideoTrack trackRef={localCam} style={styles.pipVideo} objectFit="cover" mirror />
          </View>
        ) : (
          <View style={[styles.pip, styles.pipOff, { top: insets.top + 72, right: pipRight }]}>
            <Ionicons name="videocam-off" size={22} color={colors.white} />
          </View>
        )
      )}

      {/* Floating emoji reactions (rise + fade over the stage). */}
      <ReactionOverlay reactions={reactions} onDone={removeReaction} />

      {/* Emoji reactions row — sits just above the control bar. */}
      <View style={[styles.reactionsRowWrap, { bottom: 96 + Math.max(insets.bottom, spacing.lg) }]} pointerEvents="box-none">
        <ReactionsRow onSend={sendReaction} />
      </View>

      {/* Bottom glass control bar — translucent dark glass floating over the
          stage; media reads through it. */}
      <GlassSurface
        mode="dark"
        variant="bar"
        radius={0}
        sheen={false}
        shadow={false}
        style={styles.controlBar}
        contentStyle={[styles.controlBarInner, containerStyle, { paddingBottom: Math.max(insets.bottom, spacing.lg) }]}
      >
        {/* Mute */}
        <TouchableOpacity
          style={[styles.controlBtn, !isMicOn && styles.controlBtnMuted]}
          onPress={handleToggleMic}
          disabled={togglingMic}
          activeOpacity={0.8}
          accessibilityRole="button"
          accessibilityLabel={isMicOn ? 'Mute microphone' : 'Unmute microphone'}
          accessibilityState={{ disabled: togglingMic }}
        >
          <Ionicons name={isMicOn ? 'mic' : 'mic-off'} size={24} color={isMicOn ? colors.white : colors.ink} />
        </TouchableOpacity>

        {/* Speaker toggle — audio-only calls (earpiece default ↔ loudspeaker). */}
        {isAudioOnly && (
          <TouchableOpacity
            style={[styles.controlBtn, speakerOn && styles.controlBtnActive]}
            onPress={handleToggleSpeaker}
            activeOpacity={0.8}
            accessibilityRole="button"
            accessibilityLabel={speakerOn ? 'Switch to earpiece' : 'Switch to speaker'}
            accessibilityState={{ selected: speakerOn }}
          >
            <Ionicons name={speakerOn ? 'volume-high' : 'volume-low'} size={24} color={speakerOn ? colors.ink : colors.white} />
          </TouchableOpacity>
        )}

        {/* Camera + flip controls — hidden on audio-only calls. */}
        {!isAudioOnly && (
          <>
            {/* Camera on/off */}
            <TouchableOpacity
              style={[styles.controlBtn, !isCamOn && styles.controlBtnMuted]}
              onPress={handleToggleCam}
              disabled={togglingCam}
              activeOpacity={0.8}
              accessibilityRole="button"
              accessibilityLabel={isCamOn ? 'Turn camera off' : 'Turn camera on'}
              accessibilityState={{ disabled: togglingCam }}
            >
              <Ionicons name={isCamOn ? 'videocam' : 'videocam-off'} size={24} color={isCamOn ? colors.white : colors.ink} />
            </TouchableOpacity>

            {/* Flip camera */}
            <TouchableOpacity
              style={[styles.controlBtn, flipping && styles.controlBtnDisabled]}
              onPress={handleFlipCamera}
              disabled={flipping || !isCamOn}
              activeOpacity={0.8}
              accessibilityRole="button"
              accessibilityLabel="Flip camera"
              accessibilityState={{ disabled: flipping || !isCamOn }}
            >
              <Ionicons name="camera-reverse" size={24} color={colors.white} />
            </TouchableOpacity>
          </>
        )}

        {/* Screen share (iOS broadcast). Highlighted while sharing. */}
        <TouchableOpacity
          style={[styles.controlBtn, isSharing && styles.controlBtnActive, togglingShare && styles.controlBtnDisabled]}
          onPress={handleToggleScreenShare}
          disabled={togglingShare}
          activeOpacity={0.8}
          accessibilityRole="button"
          accessibilityLabel={isSharing ? 'Stop sharing screen' : 'Share screen'}
          accessibilityState={{ disabled: togglingShare, selected: isSharing }}
        >
          <Ionicons name="phone-portrait-outline" size={22} color={isSharing ? colors.ink : colors.white} />
        </TouchableOpacity>

        {/* End call */}
        <TouchableOpacity
          style={styles.endBtn}
          onPress={onEnd}
          disabled={closing}
          activeOpacity={0.85}
          accessibilityRole="button"
          accessibilityLabel="End call"
          accessibilityState={{ disabled: closing }}
        >
          <Ionicons name="call" size={26} color={colors.white} style={styles.endIcon} />
        </TouchableOpacity>
      </GlassSurface>

      {/* CLOSING STATE: covers the live UI while the guarded teardown runs so the
          user never sees a frozen call or a blank flash on the way out. */}
      {closing && (
        <View style={styles.closingOverlay} pointerEvents="auto">
          <ParticleBackground theme="dark" intensity={0.3} count={4} />
          <ActivityIndicator size="large" color={colorTokens.dark.primary} />
          <Text style={styles.closingText}>Ending call…</Text>
        </View>
      )}
    </View>
  );
}

const PIP_W = 104;
const PIP_H = 150;

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: colorTokens.dark.bg,
  },
  container: {
    flex: 1,
    backgroundColor: colorTokens.dark.bg,
  },
  center: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: spacing.md,
    padding: spacing.xxxl,
  },
  closingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.md,
    backgroundColor: colorTokens.dark.bg + 'F2',
  },
  closingText: {
    fontSize: 16,
    letterSpacing: -0.1,
    color: colorTokens.dark.text,
    ...fonts.semibold,
  },
  errorText: {
    fontSize: 15,
    lineHeight: 21,
    color: colorTokens.dark.textMuted,
    textAlign: 'center',
    ...fonts.medium,
  },
  endedTitle: {
    fontSize: 24,
    lineHeight: 28,
    letterSpacing: -0.4,
    color: colors.white,
    textAlign: 'center',
    ...fonts.bold,
  },
  backBtn: {
    paddingHorizontal: spacing.xxl,
    paddingVertical: spacing.md + 2,
    backgroundColor: colors.primary,
    borderRadius: radius.full,
    marginTop: spacing.md,
    shadowColor: colors.primary,
    shadowOpacity: 0.4,
    shadowRadius: 18,
    shadowOffset: { width: 0, height: 6 },
  },
  backBtnText: {
    fontSize: 15,
    letterSpacing: 0.1,
    color: colors.white,
    ...fonts.semibold,
  },
  remoteVideo: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: '#000',
  },
  remotePlaceholder: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colorTokens.dark.bgMedia,
  },
  // Soft glow ring cradling the avatar so it reads as lit by the aurora behind.
  remoteAvatarRing: {
    width: 156,
    height: 156,
    borderRadius: 78,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.16)',
    backgroundColor: 'rgba(255,255,255,0.04)',
    shadowColor: colors.primaryLight,
    shadowOpacity: 0.45,
    shadowRadius: 40,
    shadowOffset: { width: 0, height: 0 },
  },
  remoteAvatar: {
    width: 124,
    height: 124,
    borderRadius: 62,
  },
  remoteAvatarFallback: {
    backgroundColor: colors.primary + '40',
    alignItems: 'center',
    justifyContent: 'center',
  },
  remoteAvatarLetter: {
    fontSize: 46,
    letterSpacing: -0.5,
    color: colors.white,
    ...fonts.bold,
  },
  topBar: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
  },
  topBarInner: {
    paddingHorizontal: spacing.lg,
    paddingBottom: spacing.md,
    alignItems: 'center',
    borderTopWidth: 0,
    borderLeftWidth: 0,
    borderRightWidth: 0,
  },
  peerName: {
    fontSize: 19,
    letterSpacing: -0.3,
    color: colors.white,
    ...fonts.bold,
  },
  statusRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginTop: 2,
  },
  liveDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: colors.success,
  },
  statusText: {
    fontSize: 13,
    letterSpacing: 0.2,
    color: colorTokens.dark.textMuted,
    ...fonts.semibold,
  },
  pip: {
    position: 'absolute',
    right: spacing.lg,
    width: PIP_W,
    height: PIP_H,
    borderRadius: radius.lg,
    overflow: 'hidden',
    borderWidth: 1.5,
    borderColor: 'rgba(255,255,255,0.25)',
    backgroundColor: '#000',
  },
  pipVideo: {
    width: '100%',
    height: '100%',
  },
  pipOff: {
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(26,26,46,0.9)',
  },
  controlBar: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
  },
  controlBarInner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.lg,
    gap: spacing.lg,
    borderBottomWidth: 0,
    borderLeftWidth: 0,
    borderRightWidth: 0,
  },
  controlBtn: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: 'rgba(255,255,255,0.14)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.14)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  controlBtnMuted: {
    backgroundColor: 'rgba(255,255,255,0.85)',
  },
  controlBtnActive: {
    backgroundColor: 'rgba(255,255,255,0.85)',
  },
  controlBtnDisabled: {
    opacity: 0.5,
  },
  // The ReplayKit picker is driven programmatically; keep it off-screen + inert.
  hiddenPicker: {
    position: 'absolute',
    width: 0,
    height: 0,
    opacity: 0,
  },
  endBtn: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: colors.error,
    alignItems: 'center',
    justifyContent: 'center',
  },
  endIcon: {
    transform: [{ rotate: '135deg' }],
  },
  // ── Emoji reactions ──
  reactionOverlay: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 140,
    alignItems: 'center',
    justifyContent: 'flex-end',
    height: 300,
  },
  risingEmoji: {
    position: 'absolute',
    bottom: 0,
    fontSize: 34,
  },
  reactionsRowWrap: {
    position: 'absolute',
    left: 0,
    right: 0,
    alignItems: 'center',
  },
  reactionsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: radius.full,
    backgroundColor: 'rgba(26,26,46,0.74)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.14)',
  },
  reactionBtn: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  reactionEmoji: {
    fontSize: 24,
  },
});
